import {
  AirVent,
  FileText,
  ClipboardCheck,
  Laptop,
  Users,
  UserCheck,
  Trophy,
  GraduationCap,
  Compass,
  ShoppingBag,
  Shirt,
} from "lucide-react";
import { SectionTitle } from "@/components/site/Features";

const amenities = [
  { icon: AirVent, title: "AC Classrooms", desc: "Comfortable, distraction-free learning environment." },
  { icon: FileText, title: "Detailed Notes", desc: "Topic-wise printed notes for every subject." },
  { icon: ClipboardCheck, title: "Weekly / Monthly / Yearly Tests", desc: "Continuous assessment & feedback loop." },
  { icon: Laptop, title: "E-Learning Support", desc: "Recorded sessions and digital revision aids." },
  { icon: Users, title: "Limited Batch Size", desc: "Small groups for focused, personal teaching." },
  { icon: UserCheck, title: "One-to-One Attention", desc: "Individual mentoring for every student." },
  { icon: GraduationCap, title: "Experienced Teachers", desc: "Subject specialists with years of board record." },
  { icon: Compass, title: "Subject Guidance", desc: "Stream & career counselling, exam strategy." },
];

const placeholders = [
  {
    id: "felicitation",
    title: "Felicitation Programs",
    desc: "Annual award ceremonies recognising top performers and consistent students.",
    src: "https://customer-assets.emergentagent.com/job_vitawa-classes/artifacts/g651auoc_IMG-20260428-WA0002.jpg",
  },
  {
    id: "bag",
    title: "Gurukul Bag",
    desc: "Custom institute bag (placeholder — replace with actual photo).",
    icon: ShoppingBag,
  },
  {
    id: "tshirt",
    title: "Gurukul T-Shirt",
    desc: "Branded student tee for events (placeholder — replace with actual photo).",
    icon: Shirt,
  },
];

export default function Amenities() {
  return (
    <section
      id="amenities"
      data-testid="amenities-section"
      className="bg-slate-50 py-16 md:py-24"
    >
      <div className="container mx-auto px-4 md:px-8">
        <SectionTitle
          eyebrow="Amenities"
          title="Everything a serious learner actually needs."
          subtitle="No flash. Just the right environment, the right material, and the right attention."
        />

        {/* Icon grid */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {amenities.map((a) => {
            const Icon = a.icon;
            return (
              <div
                key={a.title}
                data-testid={`amenity-${a.title.replace(/\s+/g, "-").toLowerCase()}`}
                className="group rounded-md border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-red-200 hover:shadow-md"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-red-50 text-red-600 transition-colors group-hover:bg-red-600 group-hover:text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <h4 className="mt-4 font-display text-[15px] font-bold text-slate-900 md:text-base">
                  {a.title}
                </h4>
                <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
                  {a.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Placeholders block */}
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {placeholders.map((p) => (
            <div
              key={p.id}
              data-testid={`placeholder-${p.id}`}
              className="overflow-hidden rounded-md border border-slate-200 bg-white"
            >
              <div className="relative h-44 w-full overflow-hidden bg-gradient-to-br from-red-50 via-orange-50 to-white">
                {p.src ? (
                  <img
                    src={p.src}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-contain p-2"
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center text-red-600">
                    {p.icon && <p.icon className="h-12 w-12" strokeWidth={1.6} />}
                    <span className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Photo Placeholder
                    </span>
                  </div>
                )}
                <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-red-700 shadow-sm">
                  <Trophy className="h-3 w-3" />
                  Gurukul
                </span>
              </div>
              <div className="p-5">
                <h4 className="font-display text-base font-bold text-slate-900">
                  {p.title}
                </h4>
                <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
                  {p.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
